import { Dialog } from '../components/Dialog'
import { t } from '../i18n'
import type { MarketplaceItem } from '../api/types'
import { formatPoints } from './marketplace-model'

/** How the reward's discount reaches the sale. Both are always offered (Figma 2362:4446): the
 * cashier knows whether the item is already rung up, and the earlier auto-detect variant
 * (2264:2378, single "Confirmer") was superseded by this one. */
export type ClaimMode = 'offer' | 'add'

/** "Utiliser la récompense ?" — the confirm step between tapping a marketplace card and spending
 * the points (Figma 2362:4446), and its undo counterpart.
 *
 * Shows the cost and the resulting balance BEFORE anything moves, because this is the last point
 * at which the cashier can back out: past here the points are gone and a real discount is on a
 * live sale.
 *
 * - "Offrir le produit dans la commande" → the item is already on the ticket; discount that line.
 * - "Ajouter le produit dans la commande" → add the item to the sale, then discount the new line.
 *
 * "Offrir" is only rendered when the item IS on the ticket (`canOffer`). Offering it otherwise is
 * not a choice the cashier can make — there is no line to discount — so showing it just invites a
 * tap that has to be undone. When the item is absent, adding it is the only real action, and it
 * gets the full width.
 *
 * In `revoke` mode the same card is already claimed, so the dialog reads as a refund (+ points)
 * and offers a single undo. Confirming rather than undoing on the bare second tap is deliberate:
 * the cashier has already tapped this card once, and an unconfirmed re-tap would silently reverse
 * a reward the customer may already be holding.
 */
export function ClaimConfirmDialog({
  item,
  balance,
  canOffer,
  revoke = false,
  busy = false,
  onConfirm,
  onRevoke,
  onCancel,
}: {
  item: MarketplaceItem
  balance: number
  /** Is the reward's linked product already on the open ticket? Drives whether "Offrir" exists. */
  canOffer: boolean
  /** The reward is already claimed: show the undo variant. */
  revoke?: boolean
  busy?: boolean
  onConfirm: (mode: ClaimMode) => void
  onRevoke: () => void
  onCancel: () => void
}) {
  const balanceAfter = revoke ? balance + item.points_cost : balance - item.points_cost

  return (
    <Dialog title={revoke ? t('undoRewardQuestion') : t('useRewardQuestion')} onDismiss={onCancel}>
      <div className="mt-7 flex items-center justify-between gap-6 rounded-lg bg-brand-50 px-6 py-[26px]">
        <span className="min-w-0 truncate text-[24px] font-semibold text-ink">{item.label}</span>
        <span className="shrink-0 text-[24px] font-semibold text-brand">
          {revoke ? '+' : '−'} {formatPoints(item.points_cost)} {t('pts')}
        </span>
      </div>

      <p className="mt-6 text-[19px] font-medium text-ink-soft">
        {t('balanceAfter')} : {formatPoints(balanceAfter)} {t('pts')}
      </p>

      {revoke ? (
        <button
          type="button"
          disabled={busy}
          onClick={onRevoke}
          className="mt-14 w-full rounded-lg bg-brand px-6 py-[18px] text-[21px] font-semibold text-white transition hover:bg-brand-dark disabled:opacity-60"
        >
          {t('undoReward')}
        </button>
      ) : (
        <div className="mt-14 flex gap-4">
          {canOffer && (
            <button
              type="button"
              disabled={busy}
              onClick={() => onConfirm('offer')}
              className="flex-1 rounded-lg bg-brand px-6 py-[18px] text-[21px] font-semibold text-white transition hover:bg-brand-dark disabled:opacity-60"
            >
              {t('offerProductInOrder')}
            </button>
          )}
          <button
            type="button"
            disabled={busy}
            onClick={() => onConfirm('add')}
            className="flex-1 rounded-lg bg-brand px-6 py-[18px] text-[21px] font-semibold text-white transition hover:bg-brand-dark disabled:opacity-60"
          >
            {t('addProductToOrder')}
          </button>
        </div>
      )}

      <button
        type="button"
        disabled={busy}
        onClick={onCancel}
        className="mt-5 w-full rounded-lg border border-line bg-white py-[18px] text-[21px] font-semibold text-ink-soft transition hover:bg-canvas disabled:opacity-60"
      >
        {revoke ? t('back') : t('cancel')}
      </button>
    </Dialog>
  )
}
